import { useState } from "react";
import { motion } from "framer-motion";
import {
  ExternalLink, KeyRound, ShieldCheck, Globe2, CreditCard, Code2,
  BookOpen, MessageCircle, Copy, Check,
} from "lucide-react";
import StatusPill from "./components/StatusPill";
import { useMerchantApplication } from "../../context/MerchantApplicationContext";

/**
 * Brief 3.6.4 — Neptune Pay integration documentation page.
 *
 * Founder-locked Feb 18 2026: docs source-of-truth is Neptune's own
 * hosted spec at https://neptunepay.api-docs.info (was PayTech's spec
 * upstream; we've shipped our own facade). We don't mirror — drift is
 * worse than one external hop. Inline blocks answer the most common
 * questions sourced from the manual already in /app/backend/data/
 * nerea_kb/.
 *
 * Visibility: nav item only renders for status ∈ {approved, live};
 * this page returns a quiet locked card for anything earlier.
 */

const SANDBOX_BASE = "https://engine-sandbox.pay.tech";
const PRODUCTION_BASE = "https://engine.pay.tech";
const NEPTUNE_DOCS_URL = "https://neptunepay.api-docs.info";

export default function IntegrationPage() {
  const { application, loading } = useMerchantApplication();

  if (loading || !application) {
    return <div data-testid="integration-loading" className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading…</div>;
  }
  const isLive = application.status === "live";
  const isApproved = application.status === "approved";
  const accessOk = isLive || isApproved;

  if (!accessOk) {
    return (
      <div data-testid="integration-locked" className="max-w-2xl">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Integration</div>
        <h2 className="font-display mt-2 text-3xl tracking-tighter chrome-text leading-tight">Integration</h2>
        <div className="mt-8 rounded-3xl border border-white/[0.08] bg-white/[0.015] p-8 text-center">
          <BookOpen className="h-6 w-6 text-zinc-500 mx-auto" />
          <div className="mt-3 text-white text-[14px]">Integration docs unlock once you're approved.</div>
          <div className="mt-2 text-zinc-500 text-[12.5px]">Finish your application — the Dashboard tells you what's next.</div>
        </div>
      </div>
    );
  }

  const env = isLive ? "live" : "sandbox";

  return (
    <div data-testid="integration-page" className="max-w-4xl space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Integration</div>
          <h2 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
            Connect to Neptune Pay
          </h2>
          <p className="mt-3 max-w-xl text-[13.5px] text-zinc-400 leading-relaxed">
            Everything your engineers need to integrate. The full API reference is one click away — this page is your fast lane to the answers they'll ask first.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={application.status} />
          <div className="ref-mono text-[11px] text-zinc-500">{application.ref}</div>
        </div>
      </div>

      <EnvironmentBanner env={env} />

      <section data-testid="integration-quick-reference" className="space-y-4">
        <h3 className="font-display text-[18px] tracking-tight text-white">Quick reference</h3>
        <p className="text-[12.5px] text-zinc-500">Snippets pulled from the Neptune Pay API spec (latest revision in our knowledge base).</p>

        <Block icon={KeyRound} title="Authenticate" testId="integration-auth">
          <p>Every authenticated request carries your <code className="text-amber-200">Shop API Key</code> as a Bearer token.</p>
          <CodeSnippet
            testId="integration-auth-snippet"
            code={`Authorization: Bearer <SHOP_API_KEY>`}
          />
          <p className="text-zinc-500 text-[12px]">Sandbox keys and production keys are different — don't mix them. Reveal yours in <a href="/app/gateway" className="text-cyan-300 hover:text-cyan-200">Gateway</a>.</p>
        </Block>

        <Block icon={ShieldCheck} title="Verify webhook signatures" testId="integration-webhook">
          <p>Webhook payloads include a <code className="text-amber-200">Signature</code> header — an HMAC-SHA256 of the raw request body keyed with your <code className="text-amber-200">Shop Signing Key</code>.</p>
          <CodeSnippet
            testId="integration-webhook-snippet"
            code={`Signature: <HMAC-SHA256(raw_request_body, SHOP_SIGNING_KEY)>`}
          />
          <p className="text-zinc-500 text-[12px]">Compute the HMAC over the raw bytes (don't reformat the JSON first). Compare in constant time. Reject mismatches.</p>
        </Block>

        <Block icon={Globe2} title="Base URLs" testId="integration-bases">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <CodeSnippet
              testId="integration-sandbox-url"
              label="Sandbox"
              code={SANDBOX_BASE}
            />
            <CodeSnippet
              testId="integration-production-url"
              label="Production"
              code={PRODUCTION_BASE}
            />
          </div>
          <p className="text-zinc-500 text-[12px] mt-3">Sandbox first — always confirm the host before flipping to production.</p>
        </Block>

        <Block icon={CreditCard} title="Sandbox test cards" testId="integration-test-cards">
          <p className="text-zinc-400">The Neptune Pay sandbox accepts these card numbers (any future expiry, any 3-digit CVV):</p>
          <div className="mt-3 overflow-x-auto rounded-xl border border-white/[0.06]">
            <table className="w-full text-[12px]" data-testid="integration-test-cards-table">
              <thead className="bg-white/[0.02] text-zinc-500 text-[10.5px] uppercase tracking-[0.18em]">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Outcome</th>
                  <th className="text-left px-3 py-2 font-normal">Visa</th>
                  <th className="text-left px-3 py-2 font-normal">Mastercard</th>
                </tr>
              </thead>
              <tbody className="font-mono text-zinc-200">
                <tr className="border-t border-white/[0.04]"><td className="px-3 py-2 font-body text-zinc-400">Success · 3DS</td><td className="px-3 py-2">4000 0000 0000 0002</td><td className="px-3 py-2">5555 0000 0000 0008</td></tr>
                <tr className="border-t border-white/[0.04]"><td className="px-3 py-2 font-body text-zinc-400">Decline · 3DS</td><td className="px-3 py-2">4242 4242 4242 4242</td><td className="px-3 py-2">5555 0000 0000 0438</td></tr>
                <tr className="border-t border-white/[0.04]"><td className="px-3 py-2 font-body text-zinc-400">Success · non-3DS</td><td className="px-3 py-2">4000 0000 0000 0408</td><td className="px-3 py-2">5555 0000 0000 0107</td></tr>
                <tr className="border-t border-white/[0.04]"><td className="px-3 py-2 font-body text-zinc-400">Decline · non-3DS</td><td className="px-3 py-2">4000 0000 0000 0416</td><td className="px-3 py-2">5555 0000 0000 0115</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-zinc-500 text-[12px] mt-3">Sandbox amount caps: deposits &lt; 10,000,000 · withdrawals &amp; refunds &lt; 10,000.</p>
        </Block>

        <Block icon={Code2} title="First-call example" testId="integration-first-call">
          <p>Create a sandbox payment and confirm your Bearer auth works:</p>
          <CodeSnippet
            testId="integration-first-call-snippet"
            multiline
            code={`curl -X POST ${SANDBOX_BASE}/api/v1/payments \\
  -H "Authorization: Bearer $SHOP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "10.00",
    "currency": "USD",
    "paymentMethod": "BASIC_CARD",
    "referenceId": "neptune-test-1"
  }'`}
          />
          <p className="text-zinc-500 text-[12px]">A successful response returns a <code className="text-amber-200">CHECKOUT</code> state with a <code className="text-amber-200">payments/{`{id}`}</code> URL — fetch it to confirm.</p>
        </Block>
      </section>

      <section data-testid="integration-full-docs" className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400/[0.05] via-cyan-400/[0.02] to-transparent p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <BookOpen className="h-5 w-5 text-cyan-300 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-[18px] tracking-tight text-white">Full API documentation</h3>
            <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
              The complete Neptune Pay API reference. Every endpoint, every schema, every error code. Share with your tech team — bookmark it.
            </p>
            <a
              href={NEPTUNE_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="integration-neptune-docs-link"
              className="mt-5 group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] px-4 py-2.5 text-[13px] text-cyan-100 hover:bg-cyan-400/[0.12] transition-colors"
            >
              Open neptunepay.api-docs.info
              <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </section>

      <section data-testid="integration-support" className="rounded-2xl border border-white/[0.08] bg-white/[0.01] p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <MessageCircle className="h-5 w-5 text-zinc-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-[16px] tracking-tight text-white">Stuck? Ask Nerea.</h3>
            <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
              Our underwriting & integration assistant has the full Neptune Pay manual loaded. Ask in plain English.
            </p>
            <button
              type="button"
              disabled
              data-testid="integration-ask-nerea-btn"
              title="Available in the next release — your account exec is reachable on Telegram in the meantime."
              className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-white/[0.06] bg-white/[0.01] px-4 py-2 text-[12.5px] text-zinc-500 cursor-not-allowed"
            >
              Ask Nerea
              <span className="ml-1 rounded-full border border-white/[0.06] bg-white/[0.02] px-2 py-0.5 text-[9.5px] uppercase tracking-[0.18em] text-zinc-500" data-testid="integration-ask-nerea-coming-soon">Coming soon</span>
            </button>
          </div>
        </div>
      </section>

      <p className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-600">
        Reference: Neptune Pay API spec · revised Feb 2026
      </p>
    </div>
  );
}


function EnvironmentBanner({ env }) {
  const isLive = env === "live";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={`integration-env-banner-${env}`}
      className={`rounded-2xl border p-5 ${
        isLive
          ? "border-emerald-400/30 bg-emerald-400/[0.05]"
          : "border-amber-400/30 bg-amber-400/[0.05]"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-1 inline-block h-2 w-2 rounded-full ${
          isLive ? "bg-emerald-300" : "bg-amber-300"
        } animate-pulse`} />
        <div className="flex-1">
          <div className={`text-[10.5px] uppercase tracking-[0.22em] ${
            isLive ? "text-emerald-300" : "text-amber-300"
          }`}>
            {isLive ? "Live integration" : "Sandbox testing"}
          </div>
          <div className="mt-1 text-white text-[14px]">
            {isLive
              ? "You're operating against Neptune Pay production. Real money, real settlements."
              : "You're approved for go-live but haven't revealed your credentials yet. Test against sandbox first — switch to production by revealing in Gateway."}
          </div>
        </div>
      </div>
    </motion.div>
  );
}


function Block({ icon: Icon, title, testId, children }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-cyan-300" />
        <h4 className="font-display text-[15px] tracking-tight text-white">{title}</h4>
      </div>
      <div className="mt-3 space-y-3 text-[13px] text-zinc-300 leading-relaxed">
        {children}
      </div>
    </div>
  );
}


function CodeSnippet({ code, label, multiline = false, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* noop */ }
  };
  return (
    <div className="rounded-xl border border-white/[0.06] bg-black/40 overflow-hidden">
      {label && (
        <div className="px-3 py-1.5 border-b border-white/[0.04] text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{label}</div>
      )}
      <div className="flex items-start gap-2 px-3 py-2.5">
        <pre data-testid={testId}
             className={`flex-1 text-[12px] text-zinc-200 font-mono ${multiline ? "whitespace-pre overflow-x-auto" : "whitespace-pre-wrap break-all"}`}>{code}</pre>
        <button type="button" onClick={onCopy}
                title="Copy"
                className="shrink-0 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-zinc-400 hover:text-white hover:border-white/[0.14] transition-colors">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
